import { observer } from "owa-mobx-react";
import { getSelectedNode } from "owa-mail-folder-forest-store";
import { type FolderForestNodeType } from "owa-favorites-types";
import { isSearchFolderExpired } from "owa-folders";
import { getIsSearchTableShown, getSafeSelectedTableView } from "owa-mail-list-store";
import { shouldShowFolderPane, shouldShowReadingPane, toggleFolderPaneExpansion, } from "owa-mail-layout";
import { type StateChangeSource } from "owa-left-pane-layout/lib/types/StateChangeSource";
import { isReadingPaneStackedView } from "owa-mail-organization-settings/lib/utils/readingPaneStackedViewUtils";
import { getComposeViewState } from "owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState";
import getConversationReadingPaneViewState from "owa-mail-reading-pane-store-conversation/lib/utils/getConversationReadingPaneViewState";
import getItemReadingPaneViewState from "owa-mail-reading-pane-store/lib/utils/getItemReadingPaneViewState";
import getSelectedFolder from "owa-mail-store/lib/utils/getSelectedFolder";
import { StatusBarCounts, useStatusBarViewShortcutsContext, useStatusBarZoomContext, } from "owa-status-bar";
import { getIsNonMailEntitySearchTab } from "owa-tabbed-search";
import React from "react";

export const MailStatusBarItems: React.FunctionComponent = observer(function MailStatusBarItems() {
    const { updateIsEnabled: updateStatusBarZoomEnabled } = useStatusBarZoomContext();
    const { updateViewState } = useStatusBarViewShortcutsContext();
    const isNormalView = shouldShowFolderPane();
    const onViewChanged = React.useCallback((showFolderPane: boolean) => {
        toggleFolderPaneExpansion(15, true, showFolderPane);
    }, []);
    
    React.useEffect(() => {
        updateViewState({ isNormalView, onViewChanged });
    }, [isNormalView, onViewChanged, updateViewState]);
    React.useEffect(() => () => updateViewState(undefined), [updateViewState]);
    
    const isSearchTableShown = getIsSearchTableShown();
    const isNonMailEntitySearch = isSearchTableShown && getIsNonMailEntitySearchTab();
    const tableView = getSafeSelectedTableView();
    const hasSingleSelectedRow = tableView?.selectedRowKeys?.size === 1;
    const hasComposeViewState = !!getComposeViewState();
    // Check only the view-state store used by the current message or conversation view.
    const hasLoadedViewState = !!tableView &&
        (isReadingPaneStackedView(tableView.tableQuery?.listViewType)
            ? !!getConversationReadingPaneViewState()
            : !!getItemReadingPaneViewState());
    // Zoom is usable when the reading pane contains either compose or a loaded message.
    const hasZoomableReadingPaneContent = !isNonMailEntitySearch &&
        (hasComposeViewState ||
            (shouldShowReadingPane() && hasSingleSelectedRow && hasLoadedViewState));
    
    React.useEffect(() => {
        updateStatusBarZoomEnabled(hasZoomableReadingPaneContent);
    }, [hasZoomableReadingPaneContent, updateStatusBarZoomEnabled]);
    
    // Reset zoom availability only when Mail unmounts, not whenever reading-pane state changes.
    React.useEffect(() => () => {
        updateStatusBarZoomEnabled(false);
    }, [updateStatusBarZoomEnabled]);
    
    // Search shows result counts; regular Mail views show the selected folder's counts.
    let counts: React.ReactNode = null;
    if (isSearchTableShown) {
        if (!isNonMailEntitySearch && tableView) {
            counts = <StatusBarCounts items={tableView.rowKeys.length}/>;
        }
    }
    else {
        const selectedNode = getSelectedNode();
        if (selectedNode.type === 0 ||
            selectedNode.type === 5) {
            const folder = getSelectedFolder();
            if (folder && !isSearchFolderExpired(selectedNode.id)) {
                counts = (<StatusBarCounts items={folder.totalMessageCount} unread={folder.UnreadCount}/>);
            }
        }
    }
    
    return counts;
}, "MailStatusBarItems");
